/** Environment-wide Dataverse search metadata, never agent grounding or ingestion proof. */
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { ApiError, ConfigurationError, PowerAutomateError } from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

const MAX_BYTES = 1024 * 1024;
const IDENTIFIER = /^[a-zA-Z][a-zA-Z0-9_]{0,127}$/;
const LIMITATIONS =
  "Environment-wide Dataverse search metadata only. Not an agent-specific status, knowledge-source ingestion status, source-data access check, publication check, or runtime grounding proof. Sync markers are opaque service values; no freshness or completeness inference is made. Missing metadata is not zero or an empty collection.";
const annotations = { readOnlyHint: true, destructiveHint: false, idempotentHint: true };

export const getCopilotKnowledgeIndexStatusInputSchema = z
  .object({
    tableLogicalName: z.string().regex(IDENTIFIER).optional(),
    limit: z.number().int().min(1).max(200).default(25),
    fieldLimit: z.number().int().min(1).max(100).default(20),
  })
  .strict();
export const getCopilotKnowledgeIndexStatisticsInputSchema = z.object({}).strict();

export const getCopilotKnowledgeIndexStatusTool = {
  name: "get_copilot_knowledge_index_status",
  description:
    "Read environment-wide Dataverse search index status, table sync markers, indexed-column metadata, and relationship sync metadata. Optional tableLogicalName filters returned metadata locally; limit (default 25, max 200) and fieldLimit (default 20, max 100) bound output with explicit truncation. This is not agent-specific, knowledge-source ingestion status, or proof of runtime grounding. Missing and unknown values remain explicit; opaque sync markers do not establish freshness. Never queries record content, changes configuration, or starts indexing.",
  inputSchema: {
    type: "object" as const,
    properties: {
      tableLogicalName: { type: "string", pattern: IDENTIFIER.source },
      limit: { type: "integer", minimum: 1, maximum: 200, default: 25 },
      fieldLimit: { type: "integer", minimum: 1, maximum: 100, default: 20 },
    },
    additionalProperties: false,
  },
  annotations,
};

export const getCopilotKnowledgeIndexStatisticsTool = {
  name: "get_copilot_knowledge_index_statistics",
  description:
    "Read environment-wide Dataverse search index document count and storage sizes through the documented searchstatistics function. Counts describe the whole search index, not one agent, knowledge source, transcript set, or Copilot credit consumption. Missing values are unavailable, never zero; unsafe numeric precision is rejected. Does not query records, start indexing, or prove ingestion or runtime grounding.",
  inputSchema: { type: "object" as const, properties: {}, additionalProperties: false },
  annotations,
};

function invalid(): never {
  throw new ApiError(
    "Dataverse search metadata is malformed or exceeds safe bounds; no index verdict returned",
    502,
  );
}

function record(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return invalid();
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return invalid();
  return value as Record<string, unknown>;
}

/** O(n), bounded before serialization; also rejects synthetic accessors/cycles/non-JSON values. */
function inspectJson(value: unknown): void {
  const pending: Array<{ value: unknown; depth: number }> = [{ value, depth: 0 }];
  const seen = new Set<object>();
  let nodes = 0;
  while (pending.length > 0) {
    const item = pending.pop()!;
    if (++nodes > 20_000 || item.depth > 24) return invalid();
    if (item.value === null || typeof item.value === "boolean") continue;
    if (typeof item.value === "string") {
      if (Buffer.byteLength(item.value, "utf8") > MAX_BYTES) return invalid();
      continue;
    }
    if (typeof item.value === "number") {
      if (!Number.isFinite(item.value)) return invalid();
      continue;
    }
    if (typeof item.value !== "object" || seen.has(item.value)) return invalid();
    seen.add(item.value);
    if (Array.isArray(item.value)) {
      if (Object.getPrototypeOf(item.value) !== Array.prototype || item.value.length > 20_000)
        return invalid();
    } else record(item.value);
    if (Object.getOwnPropertySymbols(item.value).length > 0) return invalid();
    const descriptors = Object.getOwnPropertyDescriptors(item.value);
    if (Array.isArray(item.value) && Object.keys(descriptors).length !== item.value.length + 1)
      return invalid();
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (Array.isArray(item.value) && key === "length") continue;
      if (Array.isArray(item.value) && !/^(0|[1-9]\d*)$/.test(key)) return invalid();
      if (
        ["__proto__", "prototype", "constructor"].includes(key) ||
        !Object.hasOwn(descriptor, "value")
      )
        return invalid();
      pending.push({ value: descriptor.value, depth: item.depth + 1 });
      if (pending.length > 20_000) return invalid();
    }
  }
  if (Buffer.byteLength(JSON.stringify(value), "utf8") > MAX_BYTES) return invalid();
}

/** The documented embedded metadata has integer numbers only. Check their original
 * lexemes before JSON.parse can round fractions, underflows, or oversized integers.
 * This is a bounded O(n) scanner, not a JSON parser; JSON.parse still checks grammar.
 */
function validateIntegerLexemes(json: string): void {
  for (let index = 0; index < json.length; index++) {
    const character = json[index];
    if (character === '"') {
      // A backslash consumes the next character, including an escaped quote or
      // backslash. Unicode escape validity remains JSON.parse's responsibility.
      index++;
      while (index < json.length && json[index] !== '"') {
        index += json[index] === "\\" ? 2 : 1;
      }
      if (index >= json.length) return invalid();
      continue;
    }
    if (character !== "-" && (character === undefined || character < "0" || character > "9"))
      continue;
    if (character === "-") index++;
    const start = index;
    while (index < json.length && json[index]! >= "0" && json[index]! <= "9") index++;
    const length = index - start;
    if (
      length === 0 ||
      length > 16 ||
      json[index] === "." ||
      json[index] === "e" ||
      json[index] === "E" ||
      (length === 16 && json.slice(start, index) > "9007199254740991")
    )
      return invalid();
    index--;
  }
}

function parseValue(payload: unknown): Record<string, unknown> {
  inspectJson(payload);
  const response = record(payload).response;
  if (typeof response !== "string" || Buffer.byteLength(response, "utf8") > MAX_BYTES)
    return invalid();
  validateIntegerLexemes(response);
  let decoded: unknown;
  try {
    decoded = JSON.parse(response);
  } catch {
    return invalid();
  }
  inspectJson(decoded);
  return record(record(decoded).value);
}

async function safeRead(run: () => Promise<unknown>): Promise<Record<string, unknown>> {
  let payload: unknown;
  try {
    payload = await run();
  } catch (error) {
    const status =
      error instanceof PowerAutomateError &&
      Number.isInteger(error.statusCode) &&
      error.statusCode !== undefined &&
      error.statusCode >= 400 &&
      error.statusCode <= 599
        ? error.statusCode
        : 502;
    throw new ApiError(
      `Copilot Dataverse search metadata read failed (HTTP ${status}); no index verdict returned`,
      status,
    );
  }
  return parseValue(payload);
}

function state(value: unknown, known: readonly string[]) {
  if (value === null || value === undefined) return { availability: "not_returned", value: null };
  if (typeof value !== "string" || value.length > 128) return invalid();
  return known.includes(value)
    ? { availability: "recognized", value }
    : { availability: "unrecognized", value: null };
}

function identifier(value: unknown): string {
  if (typeof value !== "string" || !IDENTIFIER.test(value)) return invalid();
  return value;
}

function optionalIdentifier(value: unknown): string | null {
  return value === undefined || value === null ? null : identifier(value);
}

function integer(value: unknown): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) return invalid();
  return value;
}

function objectTypeCode(value: unknown): number | null {
  // The documentation's type table says string; its JSON example uses a number.
  if (typeof value === "string") {
    if (!/^\d{1,16}$/.test(value)) return invalid();
    return integer(Number(value));
  }
  return integer(value);
}

function marker(value: unknown) {
  if (value === null || value === undefined) return { availability: "not_returned", value: null };
  if (typeof value !== "string" || value.length > 128) return invalid();
  // Preserve the documented opaque version!date markers, never parse dates or echo
  // unexpected textual content (URLs, credentials, or free-form service messages).
  return /^[0-9TtZz .,:!/+_-]{1,128}$/.test(value)
    ? { availability: "returned_opaque", value }
    : { availability: "unrecognized", value: null };
}

function nullableArray(value: unknown): unknown[] | null {
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) return invalid();
  return value as unknown[];
}

function fields(value: unknown, limit: number) {
  if (value === null || value === undefined)
    return { availability: "not_returned", items: null, truncated: null };
  const entries = Object.entries(record(value));
  const items = entries.map(([name, detail]) => ({
    logicalName: identifier(name),
    indexFieldName: identifier(record(detail).indexfieldname),
  }));
  return {
    availability: "returned",
    supplied: items.length,
    items: items.slice(0, limit),
    truncated: items.length > limit,
  };
}

export async function handleGetCopilotKnowledgeIndexStatus(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  const parsed = getCopilotKnowledgeIndexStatusInputSchema.parse(input === undefined ? {} : input);
  if (!context.api)
    throw new ConfigurationError("Dataverse is not configured for the current environment");
  const api = context.api;
  const value = await safeRead(() => api.getSearchIndexStatus());
  const seenTables = new Set<string>();
  const allTables =
    nullableArray(value.entitystatusresults)?.map((raw) => {
      const item = record(raw);
      const logicalName = identifier(item.entitylogicalname);
      if (seenTables.has(logicalName.toLowerCase())) return invalid();
      seenTables.add(logicalName.toLowerCase());
      return {
        logicalName,
        objectTypeCode: objectTypeCode(item.objecttypecode),
        primaryNameField: optionalIdentifier(item.primarynamefield),
        status: state(item.entitystatus, ["EntitySyncComplete"]),
        lastDataSync: marker(item.lastdatasynctimestamp),
        lastPrincipalObjectAccessSync: marker(item.lastprincipalobjectaccesssynctimestamp),
        fields: fields(item.searchableindexedfieldinfomap, parsed.fieldLimit),
      };
    }) ?? null;
  const selectedTable = parsed.tableLogicalName?.toLowerCase();
  const selectedTables =
    allTables?.filter(
      (item) => selectedTable === undefined || item.logicalName.toLowerCase() === selectedTable,
    ) ?? null;
  const seenRelationships = new Set<string>();
  const allRelationships =
    nullableArray(value.manytomanyrelationshipsyncstatus)?.map((raw) => {
      const item = record(raw);
      const name = identifier(item.relationshipName);
      if (seenRelationships.has(name.toLowerCase())) return invalid();
      seenRelationships.add(name.toLowerCase());
      const metadataId = item.relationshipMetadataId;
      if (
        metadataId !== undefined &&
        metadataId !== null &&
        (typeof metadataId !== "string" || !GUID_PATTERN.test(metadataId))
      )
        return invalid();
      return {
        name,
        metadataId: typeof metadataId === "string" ? metadataId.toLowerCase() : null,
        searchEntity: identifier(item.searchEntity),
        relatedEntity: identifier(item.relatedEntity),
        searchEntityIdAttribute: optionalIdentifier(item.searchEntityIdAttribute),
        relatedEntityIdAttribute: optionalIdentifier(item.relatedEntityIdAttribute),
        intersectEntity: optionalIdentifier(item.intersectEntity),
        searchEntityObjectTypeCode: objectTypeCode(item.searchEntityObjectTypeCode),
        lastSyncedVersion: marker(item.lastSyncedVersion),
      };
    }) ?? null;
  const selectedRelationships =
    allRelationships?.filter(
      (item) =>
        selectedTable === undefined ||
        item.searchEntity.toLowerCase() === selectedTable ||
        item.relatedEntity.toLowerCase() === selectedTable,
    ) ?? null;
  const page = <T>(all: T[] | null, selected: T[] | null) => ({
    availability: all === null ? "not_returned" : "returned",
    supplied: all?.length ?? null,
    matching: selected?.length ?? null,
    items: selected?.slice(0, parsed.limit) ?? null,
    truncated: selected === null ? null : selected.length > parsed.limit,
  });
  return JSON.stringify(
    {
      scope: "current_environment_dataverse_search_index",
      tableLogicalName: parsed.tableLogicalName ?? null,
      indexStatus: state(value.status, ["notprovisioned", "provisioninginprogress", "provisioned"]),
      lockboxStatus: state(value.lockboxstatus, [
        "Unknown",
        "Disabled",
        "Enabled",
        "DisablingInProgress",
        "EnablingInProgress",
      ]),
      customerManagedKeyStatus: state(value.cmkstatus, [
        "Unknown",
        "Disabled",
        "Enabled",
        "DisablingInProgress",
        "EnablingInProgress",
      ]),
      tables: page(allTables, selectedTables),
      relationships: page(allRelationships, selectedRelationships),
      limitations: LIMITATIONS,
    },
    null,
    2,
  );
}

export async function handleGetCopilotKnowledgeIndexStatistics(
  context: { api: DataverseApi | null },
  input: unknown,
): Promise<string> {
  getCopilotKnowledgeIndexStatisticsInputSchema.parse(input === undefined ? {} : input);
  if (!context.api)
    throw new ConfigurationError("Dataverse is not configured for the current environment");
  const api = context.api;
  const value = await safeRead(() => api.getSearchIndexStatistics());
  const metric = (raw: unknown) => {
    const number = integer(raw);
    return { availability: number === null ? "not_returned" : "returned", value: number };
  };
  return JSON.stringify(
    {
      scope: "current_environment_dataverse_search_index",
      documentCount: metric(value.documentcount),
      storageSizeInBytes: metric(value.storagesizeinbytes),
      storageSizeInMegabytes: metric(value.storagesizeinmb),
      limitations: `${LIMITATIONS} Statistics are index totals, not agent knowledge counts, source-file counts, or Copilot credit consumption.`,
    },
    null,
    2,
  );
}
